import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Lock, Save, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import DiditSettings from "../../components/DiditSettings";

const emptyForm = {
  old_password: "",
  new_password: "",
  confirm_password: "",
};

export default function AdminSettings() {
  const nav = useNavigate();
  const { logout, user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      toast.error("New passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      setForm(emptyForm);
      toast.success("Password changed. Please sign in again.");
      setTimeout(() => {
        logout();
        nav("/admin", { replace: true });
      }, 500);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-white">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Manage your login and review customer verification requirements.</p>
      </div>

      <div className="mb-5 max-w-2xl rounded-lg border border-[#332a10] bg-[#101010] p-5 sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg border border-sky-400/35 bg-sky-400/10 text-sky-200">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white">Phone and ID verification for withdrawals</h2>
              <p className="text-sm leading-relaxed text-slate-500">
                Every withdrawal requires a verified phone number and ID front, ID back, and selfie approved through Didit. Duplicate identities are blocked, including for existing members.
              </p>
            </div>
          </div>
          <span className="inline-flex min-w-[116px] items-center justify-center rounded-full border border-emerald-500/35 bg-emerald-500/15 px-4 py-2 text-sm font-bold text-emerald-200">Always required</span>
        </div>
      </div>

      <DiditSettings />

      <div className="max-w-2xl rounded-lg border border-[#332a10] bg-[#101010] p-5 sm:p-6">
        <div className="mb-5 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-lg border border-[#D4AF37]/35 bg-[#D4AF37]/10 text-[#F1C40F]">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white">Admin password</h2>
            <p className="truncate text-sm text-slate-500">{user?.email || "Current admin"}</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <PasswordField
            autoComplete="current-password"
            icon={Lock}
            label="Current password"
            onChange={(value) => setForm((current) => ({ ...current, old_password: value }))}
            value={form.old_password}
          />
          <PasswordField
            autoComplete="new-password"
            icon={KeyRound}
            label="New password"
            onChange={(value) => setForm((current) => ({ ...current, new_password: value }))}
            value={form.new_password}
          />
          <PasswordField
            autoComplete="new-password"
            icon={KeyRound}
            label="Confirm new password"
            onChange={(value) => setForm((current) => ({ ...current, confirm_password: value }))}
            value={form.confirm_password}
          />

          <button
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-bold text-black hover:bg-[#F1C40F] disabled:opacity-60"
            type="submit"
          >
            <Save className="h-4 w-4" />
            {saving ? "Saving..." : "Change password"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PasswordField({ autoComplete, icon: Icon, label, onChange, value }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      <span className="relative block">
        <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          autoComplete={autoComplete}
          className="h-11 w-full rounded-xl border border-white/10 bg-[#0d0d10] px-3 pl-9 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-500 focus:border-[#D4AF37]/70"
          minLength={6}
          onChange={(event) => onChange(event.target.value)}
          required
          type="password"
          value={value}
        />
      </span>
    </label>
  );
}
